import { onlineManager } from '@tanstack/react-query'

/** Marks what stays usable while the page is frozen: the update's own modal and banners. */
export const APP_UPDATE_ATTRIBUTE = 'data-app-update'

const BLOCKED_EVENTS = [
  'click',
  // Blocked while frozen, never handled: no click competes with it here.
  // eslint-disable-next-line no-restricted-syntax
  'dblclick',
  'auxclick',
  'submit',
  'beforeinput',
  'paste',
  'cut',
  'drop',
  'dragstart',
  'keydown',
] as const

// Keys that only read: moving around, selecting, copying, reloading.
const READING_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  'Tab',
  'Escape',
  'Shift',
  'Control',
  'Meta',
  'Alt',
  'F5',
])

function isReadingKey(event: KeyboardEvent): boolean {
  if (READING_KEYS.has(event.key)) return true
  const key = event.key.toLowerCase()
  return (event.ctrlKey || event.metaKey) && (key === 'c' || key === 'a' || key === 'r')
}

function insideUpdateNotice(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(`[${APP_UPDATE_ATTRIBUTE}]`) !== null
}

/**
 * Freezes the page as it was when an update started: nothing on it can be
 * clicked, typed in, dropped on or pasted into, but it can still be scrolled,
 * read, selected and copied (unlike `inert`, which blocks selecting too). Its
 * data stops refreshing (TanStack Query is told it's offline), so it stays as
 * it was. Returns a function that thaws it.
 */
export function freezePage(): () => void {
  const block = (event: Event) => {
    if (insideUpdateNotice(event.target)) return
    if (event instanceof KeyboardEvent && isReadingKey(event)) return
    event.preventDefault()
    event.stopImmediatePropagation()
  }
  for (const type of BLOCKED_EVENTS) window.addEventListener(type, block, true)
  document.documentElement.classList.add('app-frozen')
  const wasOnline = onlineManager.isOnline()
  onlineManager.setOnline(false)
  return () => {
    for (const type of BLOCKED_EVENTS) window.removeEventListener(type, block, true)
    document.documentElement.classList.remove('app-frozen')
    onlineManager.setOnline(wasOnline)
  }
}
